<template>
  <div class="p-date-day-picker">
    <div class="mb-2">
      <slot
        name="toolbar"
        :visibleYear="innerVisibleYear"
        :visibleMonth="innerVisibleMonth"
        :focusedDay="focusedDay"
      >
        <p-row justify-center align-center nowrap>
          <p-button v-if="!noYearSteppers" dense tertiary @click="addUnit(-1, 'year')">
            <p-icon>first_page</p-icon>
          </p-button>
          <p-button v-if="!noMonthSteppers" dense tertiary @click="addUnit(-1, 'month')">
            <p-icon>chevron_left</p-icon>
          </p-button>
          <p-text-field readonly class="p-date-day-picker-month-field" solo :value="displayMonth"></p-text-field>
          <p-date-field v-model="visibleYearDate" class="p-date-day-picker-year-field" unit="year" solo></p-date-field>
          <p-button v-if="!noMonthSteppers" dense tertiary @click="addUnit(1, 'month')">
            <p-icon>chevron_right</p-icon>
          </p-button>
          <p-button v-if="!noYearSteppers" dense tertiary @click="addUnit(1, 'year')">
            <p-icon>last_page</p-icon>
          </p-button>
        </p-row>
      </slot>
    </div>
    <p-divider></p-divider>
    <div class="p-date-picker-detail-panel">
      <div class="day-tile-row">
        <span class="day-tile day-header">#</span>
        <span v-for="dayName in dayNames" :key="dayName" class="day-tile day-header">{{ dayName }}</span>
      </div>
      <div
        tabindex="0"
        class="p-date-picker-detail-days-panel"
        @keyup.left.prevent="moveFocusByDay(-1)"
        @keyup.right.prevent="moveFocusByDay(1)"
        @keydown.up.prevent="moveFocusByDay(-7)"
        @keydown.down.prevent="moveFocusByDay(7)"
      >
        <div v-for="(week, weekIndex) in dayTiles" :key="weekIndex" class="day-tile-row">
          <p-button
            readonly
            tertiary
            :class="{
              'day-tile': true,
              'week-number': true,
              'caption-2': !week.isThisWeek,
              'text-color-text': week.isThisWeek,
              'no-select': true,
            }"
          >
            {{ week.weekNumber }}
          </p-button>
          <p-button
            v-for="date in week.days"
            :key="'x' + date.day"
            :ref="innerVisibleYear + '-' + date.month + '-' + date.day"
            tabindex="-1"
            :disabled="date.disabled"
            :tertiary="!(date.day === selectedDateDay && date.month === selectedDateMonth && innerVisibleYear === selectedDateYear)"
            :class="{'day-tile': true, 'day-tile-today': date.isToday}"
            @click="selectDate(date)"
          >
            <span :class="{'text-color-app-2': date.notInVisibleMonth}">{{ date.day }}</span>
          </p-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import Vue from 'vue';
  import type { Dayjs, ManipulateType } from 'dayjs';

  export type DayTileType = {
    day: number,
    month:number
    year:number
    isToday: boolean,
    notInVisibleMonth: boolean,
    disabled: boolean
  };

  export type WeekSummaryType = {
    weekNumber: number,
    isThisWeek: boolean,
    days: DayTileType[]
  };

  export default Vue.extend({
    props: {
      value: { default: undefined, type: String },
      visibleYear: { default: undefined, type: Number },
      visibleMonth: { default: undefined, type: Number },
      dayStartOfWeek: { type: Number, default: 1 },
      min: { type: String, default: undefined },
      max: { type: String, default: undefined },
      disabledDates: { type: Array, default: () => ([]) },
      noYearSteppers: { type: Boolean, default: false },
      noMonthSteppers: { type: Boolean, default: false },
    },
    data: () => ({
      focusedDay: Vue.$date.now().day() as number,
      innerVisibleMonth: Vue.$date.now().month() + 1 as number,
      innerVisibleYear: Vue.$date.now().year() as number,
      selectedDate: undefined as Dayjs | undefined,
      dayNames: [] as string[],
    }),
    computed: {
      visibleYearDate: {
        get() {
          return `${this.innerVisibleYear}-1-1`;
        },
        set(value: string) {
          this.innerVisibleYear = this.$date(value)?.year();
        },
      },
      selectedDateMonth() {
        if (!this.selectedDate) {
          return undefined;
        }
        return this.selectedDate.month() + 1;
      },
      selectedDateDay() {
        return this.selectedDate?.date();
      },
      selectedDateYear() {
        return this.selectedDate?.year();
      },
      displayMonth(): string {
        return this.$date(`${this.innerVisibleYear}-${this.innerVisibleMonth}-${this.focusedDay}`).format('MMMM');
      },
      dayTiles() {
        const firstDayMonthObj = this.$date(`${this.innerVisibleYear}-${this.innerVisibleMonth}-1`);
        const firstDayOfMonthNr = firstDayMonthObj.day();
        let nrDaysPreviousMonth = firstDayOfMonthNr - this.dayStartOfWeek;
        if (nrDaysPreviousMonth < 0) {
          nrDaysPreviousMonth += 7;
        }
        let currentDateObj = firstDayMonthObj.subtract(nrDaysPreviousMonth + 1, 'day');
        const computedMonthDays = {} as Record<string, WeekSummaryType>;
        // 42 / 7 = 6 Rows, which is always enough to display all dates in a month (plus overlap)
        for (let i = 0; i < 42; i += 1) {
          currentDateObj = currentDateObj.add(1, 'day');
          const day = currentDateObj.date();
          const month = currentDateObj.month() + 1;
          const year = currentDateObj.year();
          const currentWeek = Math.floor(i / 7).toString();
          computedMonthDays[currentWeek] = computedMonthDays[currentWeek] ?? [];
          const isToday = this.$date.now().isSame(currentDateObj, 'day');
          const notInVisibleMonth = month !== this.innerVisibleMonth;

          let isBeforeMin = false;
          if (this.min) {
            isBeforeMin = currentDateObj.isBefore(this.$date(this.min));
          }
          let isAfterMax = false;
          if (this.max) {
            isAfterMax = currentDateObj.isAfter(this.$date(this.max));
          }
          const disabledDateObjs = this.disabledDates.map((date) => this.$date(date as string));
          const closureCurrentDateObj = currentDateObj;
          const isDisabled = !!disabledDateObjs.find((date) => closureCurrentDateObj.isSame(date));
          const disabled = isBeforeMin || isAfterMax || isDisabled;
          computedMonthDays[currentWeek] = {
            days: computedMonthDays[currentWeek].days ?? [],
            isThisWeek: computedMonthDays[currentWeek].isThisWeek || isToday,
            weekNumber: currentDateObj.week(),
          };
          computedMonthDays[currentWeek].days.push({
            year, month, day, isToday, notInVisibleMonth, disabled,
          });
        }
        return computedMonthDays;
      },
    },
    watch: {
      visibleYear: {
        immediate: true,
        handler() {
          this.innerVisibleYear = this.visibleYear;
        },
      },
      visibleMonth: {
        immediate: true,
        handler() {
          this.innerVisibleMonth = this.visibleMonth;
        },
      },
      innerVisibleMonth() {
        this.$emit('visible-month-change', this.innerVisibleMonth);
      },
      innerVisibleYear() {
        this.$emit('visible-year-change', this.innerVisibleYear);
      },
      value: {
        immediate: true,
        handler() {
          if (this.value) {
            const dateObj = this.$date(this.value);
            this.selectedDate = dateObj;
            this.focusedDay = dateObj.date();
            this.innerVisibleMonth = dateObj.month() + 1;
            this.innerVisibleYear = dateObj.year();
          }
        },
      },
    },
    mounted() {
      if (!this.innerVisibleMonth) {
        this.innerVisibleMonth = this.$date.now().month() + 1;
        this.innerVisibleYear = this.$date.now().year();
      }
    },
    beforeMount() {
      this.dayNames = [0, 1, 2, 3, 4, 5, 6].map((dayNr) => Vue.$date.now().day(dayNr + this.dayStartOfWeek).format('dd'));
    },
    methods: {
      addUnit(amount: number, unit: ManipulateType) {
        const dateObj = this.$date(`${this.innerVisibleYear}-${this.innerVisibleMonth}-${this.focusedDay}`);
        const newDateObj = dateObj.add(amount, unit);
        this.innerVisibleMonth = newDateObj.month() + 1;
        this.innerVisibleYear = newDateObj.year();
      },
      selectDate(tile: DayTileType) {
        this.selectedDate = this.$date(`${tile.year}-${tile.month}-${tile.day}`);
        this.focusedDay = this.selectedDate.date();
        this.$emit('input', this.selectedDate.format('YYYY-MM-DD'));
      },
      moveFocusByDay(amount: number) {
        const dateObj = this.$date(`${this.innerVisibleYear}-${this.innerVisibleMonth}-${this.focusedDay}`);
        const newDateObj = dateObj.add(amount, 'day');
        this.focusedDay = newDateObj.date();
        this.innerVisibleMonth = newDateObj.month() + 1;
        this.innerVisibleYear = newDateObj.year();
        this.$nextTick(() => {
          let tileRef = this.$refs[`${this.innerVisibleYear}-${this.innerVisibleMonth}-${this.focusedDay}`] as Vue;
          if (tileRef) {
            if (Array.isArray(tileRef) && tileRef.length > 0) {
              tileRef = tileRef[0] as any;
            }
            (tileRef as any).navigatedUsingKeyboard = true;
            const el = (tileRef.$el ?? {}) as HTMLElement;
            el.focus();
          }
        });
      },
    },
  });
</script>
